import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Pencil, Phone, ShieldAlert } from "lucide-react";

import {
  updateTarget,
  type TargetListItem,
  type TargetWriteBody,
} from "../../api/targets";
import { ApiError } from "../../lib/api";
import {
  useCreateTarget,
  useTargets,
  useVerifyConfirm,
  useVerifyStart,
} from "../../hooks/useTargets";
import { Button } from "../../components/ui/Button";
import { Sheet } from "../../components/Sheet";
import { TargetDrawer } from "./Drawer";

/**
 * Test-pool management — operator self-test numbers.
 *
 * A test-pool target is created with `test_only=true`. Such a target can
 * bypass ROE on the Send-Test-Call path ONLY once its phone has been OTP
 * verified here (and is never dialable by a real campaign — enforced server
 * side). Verification places a TTS-only call that speaks a 6-digit code; the
 * operator types it back to flip the Verified badge.
 */
export function TestPool() {
  // The list endpoint already returns test_only / phone_verified, so we filter
  // client-side rather than adding a dedicated test-pool list call.
  const qc = useQueryClient();
  const { data: listData, isLoading } = useTargets("");
  const createTarget = useCreateTarget();

  const testTargets = useMemo<TargetListItem[]>(
    () => (listData?.items ?? []).filter((t) => t.test_only),
    [listData?.items],
  );

  // ─── Per-row edit (reuses the shared TargetDrawer + updateTarget) ──────────
  // Editing metadata (name/email/role/dept) leaves verification intact; editing
  // the phone resets phone_verified server-side (see update_target). We just
  // invalidate the targets list and let the badge re-render from fresh data.
  const [editing, setEditing] = useState<TargetListItem | null>(null);

  const editMut = useMutation({
    mutationFn: (vars: { id: string; patch: Partial<TargetWriteBody> }) =>
      updateTarget(vars.id, vars.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["targets"] });
      setEditing(null);
    },
  });

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newDept, setNewDept] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  function resetCreateForm() {
    setNewName("");
    setNewPhone("");
    setNewEmail("");
    setNewRole("");
    setNewDept("");
    setCreateError(null);
  }

  function submitCreate() {
    if (!newName.trim()) return;
    if (!newPhone.trim()) {
      setCreateError("A phone number (E.164) is required for a test number.");
      return;
    }
    setCreateError(null);
    createTarget.mutate(
      {
        full_name: newName.trim(),
        primary_phone_e164: newPhone.trim(),
        primary_email: newEmail.trim() || null,
        role: newRole.trim() || null,
        department: newDept.trim() || null,
        test_only: true,
      },
      {
        onSuccess: () => {
          setShowCreate(false);
          resetCreateForm();
        },
        onError: (err) => {
          setCreateError(
            err instanceof Error ? err.message : "Failed to add test number.",
          );
        },
      },
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
          Test pool
        </h3>
        <span
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          · {testTargets.length} number{testTargets.length === 1 ? "" : "s"}
        </span>
        <div style={{ flex: 1 }} />
        <Button
          variant="primary"
          onClick={() => {
            setShowCreate((v) => !v);
            setCreateError(null);
          }}
          style={{
            padding: "7px 12px",
            borderRadius: 8,
            border: "1px solid var(--accent)",
            background: "var(--accent)",
            color: "white",
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          + add test number
        </Button>
      </div>

      <p
        style={{
          margin: 0,
          fontSize: 11.5,
          lineHeight: 1.5,
          color: "var(--text-muted)",
          maxWidth: 620,
        }}
      >
        Verify your own numbers to test-call them with recording on. Verified
        test numbers bypass ROE on the Send Test Call path only — they can never
        be dialed by a real campaign.
      </p>

      {/* Add-number form */}
      {showCreate && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 16,
            display: "flex",
            gap: 10,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 160 }}>
            <FieldLabel>Label *</FieldLabel>
            <input
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setCreateError(null);
              }}
              placeholder="e.g. My cell"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCreate();
              }}
              style={createInputStyle()}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 180 }}>
            <FieldLabel>Phone (E.164) *</FieldLabel>
            <input
              value={newPhone}
              onChange={(e) => {
                setNewPhone(e.target.value);
                setCreateError(null);
              }}
              placeholder="e.g. +14155550123"
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCreate();
              }}
              style={createInputStyle(true)}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 180 }}>
            <FieldLabel>Email</FieldLabel>
            <input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="optional"
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCreate();
              }}
              style={createInputStyle(true)}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 140 }}>
            <FieldLabel>Role</FieldLabel>
            <input
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              placeholder="optional"
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCreate();
              }}
              style={createInputStyle()}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 140 }}>
            <FieldLabel>Department</FieldLabel>
            <input
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              placeholder="optional"
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCreate();
              }}
              style={createInputStyle()}
            />
          </div>
          <Button
            variant="primary"
            disabled={!newName.trim() || !newPhone.trim() || createTarget.isPending}
            onClick={submitCreate}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--accent)",
              background:
                !newName.trim() || !newPhone.trim()
                  ? "var(--bg-tertiary)"
                  : "var(--accent)",
              color:
                !newName.trim() || !newPhone.trim()
                  ? "var(--text-muted)"
                  : "white",
              whiteSpace: "nowrap",
            }}
          >
            {createTarget.isPending ? "Adding..." : "Add"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setShowCreate(false);
              resetCreateForm();
            }}
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            Cancel
          </Button>
          {createError && (
            <div
              style={{
                width: "100%",
                fontSize: 11,
                color: "var(--error)",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.04em",
              }}
            >
              {createError}
            </div>
          )}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
      ) : testTargets.length === 0 ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px dashed var(--border)",
            borderRadius: 12,
            padding: 24,
            textAlign: "center",
            fontSize: 12,
            color: "var(--text-muted)",
          }}
        >
          No test numbers yet. Add one above to validate engagement settings
          against your own phone.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {testTargets.map((t) => (
            <TestNumberRow key={t.target_id} target={t} onEdit={() => setEditing(t)} />
          ))}
        </div>
      )}

      {/* Per-row edit — costume metadata (name/email/role/dept) is freely
          editable; changing the phone clears verification server-side. */}
      <Sheet
        open={editing != null}
        onClose={() => setEditing(null)}
        title={editing?.full_name ?? "Test number"}
      >
        {editing != null && (
          <TargetDrawer
            target={editing}
            onClose={() => setEditing(null)}
            onRequestDelete={() => {}}
            onSave={(patch) =>
              editMut.mutate(
                { id: editing.target_id, patch },
                {
                  onError: (err) =>
                    alert(err instanceof Error ? err.message : "Failed to update test number."),
                },
              )
            }
            renderFooter={({ onCancel, onSave }) => (
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={onCancel} style={sheetCancelBtn()}>
                  cancel
                </button>
                <button onClick={onSave} disabled={editMut.isPending} style={sheetSaveBtn()}>
                  {editMut.isPending ? "saving…" : "save"}
                </button>
              </div>
            )}
          />
        )}
      </Sheet>
    </div>
  );
}

// ─── One test number ─────────────────────────────────────────────────────────────

function TestNumberRow({ target, onEdit }: { target: TargetListItem; onEdit: () => void }) {
  const verifyStart = useVerifyStart();
  const verifyConfirm = useVerifyConfirm();

  // null  = idle (not started); "code" = awaiting code entry
  const [phase, setPhase] = useState<"idle" | "code">("idle");
  const [code, setCode] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  const verified = target.phone_verified;

  function start() {
    setFeedback(null);
    verifyStart.mutate(target.target_id, {
      onSuccess: () => {
        setPhase("code");
        setCode("");
      },
      onError: (err) => setFeedback(startErrorMessage(err)),
    });
  }

  function confirm() {
    const trimmed = code.trim();
    if (!trimmed) return;
    setFeedback(null);
    verifyConfirm.mutate(
      { id: target.target_id, code: trimmed },
      {
        onSuccess: (result) => {
          if (result.verified) {
            setPhase("idle");
            setCode("");
            setFeedback(null);
          } else {
            setFeedback(confirmFailMessage(result.reason));
          }
        },
        onError: (err) =>
          setFeedback(err instanceof Error ? err.message : "Verification failed."),
      },
    );
  }

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderLeft: `2px solid ${verified ? "var(--success)" : "var(--warning)"}`,
        borderRadius: "var(--radius-card)",
        padding: "14px 16px",
        boxShadow: "var(--shadow-card)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* Top line: label + phone + badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 160 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
            {target.full_name}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11.5,
              color: "var(--text-muted)",
            }}
          >
            {target.primary_phone_e164 ?? "—"}
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <VerifiedBadge verified={verified} />
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${target.full_name}`}
          title="Edit test number"
          style={{
            display: "grid",
            placeItems: "center",
            width: 28,
            height: 28,
            borderRadius: 6,
            border: "1px solid var(--border-subtle)",
            background: "transparent",
            color: "var(--text-muted)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Pencil size={13} strokeWidth={1.8} />
        </button>
        {!verified && phase === "idle" && (
          <Button
            variant="primary"
            onClick={start}
            disabled={verifyStart.isPending}
            style={{
              padding: "7px 14px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--accent)",
              background: verifyStart.isPending ? "var(--bg-tertiary)" : "var(--accent)",
              color: verifyStart.isPending ? "var(--text-muted)" : "white",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <Phone size={13} strokeWidth={1.8} />
            {verifyStart.isPending ? "Calling…" : "Verify"}
          </Button>
        )}
      </div>

      {/* Code-entry row (after a verify call is placed) */}
      {!verified && phase === "code" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            borderTop: "1px solid var(--border-subtle)",
            paddingTop: 10,
          }}
        >
          <span style={{ fontSize: 11.5, color: "var(--text-secondary)", flex: 1, minWidth: 200 }}>
            We&apos;re calling {target.primary_phone_e164 ?? "your number"} now — enter
            the 6-digit code you hear.
          </span>
          <input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
              setFeedback(null);
            }}
            placeholder="------"
            inputMode="numeric"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") confirm();
            }}
            style={{
              ...createInputStyle(true),
              width: 110,
              letterSpacing: "0.35em",
              textAlign: "center",
              fontSize: 14,
            }}
          />
          <Button
            variant="primary"
            onClick={confirm}
            disabled={code.trim().length < 6 || verifyConfirm.isPending}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--accent)",
              background:
                code.trim().length < 6 ? "var(--bg-tertiary)" : "var(--accent)",
              color: code.trim().length < 6 ? "var(--text-muted)" : "white",
              whiteSpace: "nowrap",
            }}
          >
            {verifyConfirm.isPending ? "Checking…" : "Confirm"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setPhase("idle");
              setCode("");
              setFeedback(null);
            }}
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="ghost"
            onClick={start}
            disabled={verifyStart.isPending}
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-secondary)",
              whiteSpace: "nowrap",
            }}
          >
            Resend
          </Button>
        </div>
      )}

      {feedback && (
        <div
          style={{
            fontSize: 11,
            color: "var(--error)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.03em",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <ShieldAlert size={12} strokeWidth={1.8} />
          {feedback}
        </div>
      )}
    </div>
  );
}

// ─── Bits ─────────────────────────────────────────────────────────────────────────

function VerifiedBadge({ verified }: { verified: boolean }) {
  if (verified) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 10px",
          borderRadius: 999,
          fontSize: 10.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--success)",
          background: "color-mix(in srgb, var(--success) 12%, transparent)",
          border: "1px solid color-mix(in srgb, var(--success) 35%, transparent)",
        }}
      >
        <CheckCircle2 size={12} strokeWidth={2} />
        Verified
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "var(--warning)",
        background: "color-mix(in srgb, var(--warning) 12%, transparent)",
        border: "1px solid color-mix(in srgb, var(--warning) 35%, transparent)",
      }}
    >
      Unverified
    </span>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <span
      style={{
        fontSize: 9.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--text-muted)",
      }}
    >
      {children}
    </span>
  );
}

function createInputStyle(mono = false): React.CSSProperties {
  return {
    background: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "7px 10px",
    color: "var(--text-primary)",
    fontSize: 12,
    fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
    outline: "none",
  };
}

// ─── Error-message mapping ─────────────────────────────────────────────────────────

function startErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      return "Too many verification attempts. Wait a few minutes and try again.";
    }
    if (err.status === 409) {
      return "This number isn't a test number.";
    }
    if (err.status === 404) {
      return "Target not found.";
    }
    return err.detail || "Couldn't place the verification call.";
  }
  return err instanceof Error ? err.message : "Couldn't place the verification call.";
}

function confirmFailMessage(reason: string | null | undefined): string {
  switch (reason) {
    case "wrong":
      return "Wrong code. Check the digits and try again.";
    case "expired":
      return "That code expired. Send a new one with Resend.";
    case "locked":
      return "Too many wrong attempts. Send a new code with Resend.";
    case "no_pending_code":
      return "No active code. Tap Resend to get a new one.";
    default:
      return "Code rejected. Try Resend for a fresh code.";
  }
}

// ─── Sheet footer buttons (mirrors Groups/Detail.tsx) ───────────────────────────

function sheetCancelBtn(): React.CSSProperties {
  return {
    padding: "8px 14px",
    borderRadius: 6,
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    color: "var(--text-secondary)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    minHeight: "var(--tap-min)",
  };
}

function sheetSaveBtn(): React.CSSProperties {
  return {
    padding: "8px 16px",
    borderRadius: 6,
    background: "var(--accent)",
    border: "1px solid var(--accent)",
    color: "white",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    minHeight: "var(--tap-min)",
  };
}
